"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import Dialog from "@/app/(main)/components/Dialog";
import OfferFlyerBar from "@/app/(main)/components/OfferFlyerBar";
import OfferRelations from "@/app/(main)/components/OfferRelations";
import {
  fetchOfferByPageUrlClient,
  getOfferRelations,
  offerText,
  type OfferDetail,
} from "@/lib/offers";
import { extractStandaloneLinks } from "@/lib/htmlUtils";
import styles from "./special-offer-card.module.css";

export interface OfferItem {
  id: string;
  title: string;
  subTitle?: string;
  photo: string;
  pageUrl: string;
  /** Rich CMS HTML teaser — rendered as a plain-text excerpt on the card. */
  shortDescription?: string;
  /** CMS-authored deal flag (e.g. "SAVE 10%"). Optional — often "" or absent. */
  offerBadge?: string | null;
  /** Terms & conditions rich HTML — shown in the dialog. */
  terms?: string | null;
}

interface OffersStripProps {
  offers: OfferItem[];
  showBadge?: boolean;
  showTitle?: boolean;
  showSubtitle?: boolean;
  marginTop?: string;
  marginBottom?: string;
}

export default function OffersStrip({
  offers,
  showBadge = true,
  showTitle = true,
  showSubtitle = true,
  marginTop = "",
  marginBottom = "mb-50",
}: OffersStripProps) {
  const [openOffer, setOpenOffer] = useState<OfferItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<OfferDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cache, setCache] = useState<Record<string, OfferDetail>>({});

  // The card photo is desktop-only: on phones (<768px, the card's own
  // breakpoint) it's not rendered at all, so the image is never added to the
  // DOM nor downloaded. Defaults to false so the server / first paint match,
  // and only flips on once we confirm a non-phone viewport.
  const [showPhoto, setShowPhoto] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setShowPhoto(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  async function handleOfferClick(offer: OfferItem) {
    setOpenOffer(offer);
    setError(null);

    if (cache[offer.pageUrl]) {
      setDetail(cache[offer.pageUrl]);
      return;
    }

    setDetail(null);
    setLoading(true);
    try {
      const data = await fetchOfferByPageUrlClient(offer.pageUrl);
      if (data) {
        setDetail(data);
        setCache((prev) => ({ ...prev, [offer.pageUrl]: data }));
      } else {
        setError("Offer details are not available right now.");
      }
    } catch {
      setError("We couldn't load the offer details. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!offers?.length) return null;

  const showHeader = showBadge || showTitle || showSubtitle;
  const dialogRelations = detail ? getOfferRelations(detail) : [];
  // Downloadable flyer — same source as the Special-Offers page card, so the
  // same offer shows the same download bar wherever its dialog is opened.
  const flyer = detail?.downloadFile?.file?.trim();
  // Terms come from the detail payload; the strip row carries them too, so fall
  // back to it if a record ever omits them there.
  const terms = offerText(detail?.terms) ?? offerText(openOffer?.terms);

  return (
    <div className={`offers-strip ${marginTop} ${marginBottom}`.trim()}>
      {/* ── Section Header ── */}
      {showHeader && (
        <div className="offers-strip__header">
          {showBadge && (
            <span className="offers-strip__badge">
              <i className="fa-solid fa-fire" />
              Limited Deals
            </span>
          )}
          {showTitle && <h3 className="offers-strip__title">Special Offers</h3>}
          {showSubtitle && (
            <p className="offers-strip__subtitle">
              Exclusive deals you don&apos;t want to miss
            </p>
          )}
        </div>
      )}

      {/* ── Cards Grid ── */}
      <div className="row g-3">
        {offers.map((offer, idx) => {
          const solo = offers.length === 1;
          const raw = offer.shortDescription?.trim() ?? "";
          // Pull any standalone "call-to-action" links out of the copy so they
          // can be rendered as buttons; `descHtml` is the remaining teaser.
          const { html: descHtml, links: ctaLinks } =
            extractStandaloneLinks(raw);
          // CMS-authored deal flag, shown as the red eyebrow pill when set.
          const badge = offerText(offer.offerBadge);
          const subTitle = offerText(offer.subTitle);
          const open = () => handleOfferClick(offer);

          return (
            <div key={offer.id} className={solo ? "col-12" : "col-md-6"}>
              {/* The card itself is NOT a button: the short description renders
                  as real HTML (it may contain its own <a> links), so a button
                  root would be invalid. The photo, title and CTA are the
                  dialog triggers instead. */}
              <div
                className={`rp-card${solo ? " rp-card--solo" : ""}`}
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                {/* Photo stub (opens dialog). The <Image> is rendered only on
                    ≥768px, so on phones the image is never loaded nor placed in
                    the DOM. The stub itself is collapsed via CSS on phones, and
                    keeps its reserved width on desktop so the post-hydration
                    image causes no layout shift. */}
                <button
                  type="button"
                  className="rp-stub"
                  onClick={open}
                  aria-label={offer.title}
                  tabIndex={showPhoto ? 0 : -1}
                >
                  {showPhoto && (
                    <Image
                      src={offer.photo || "/main/img/blank.png"}
                      alt={offer.title}
                      fill
                      sizes={solo ? "360px" : "120px"}
                      style={{ objectFit: "cover" }}
                      unoptimized
                    />
                  )}
                </button>

                {/* Red ticket spine */}
                <span className="rp-seam" aria-hidden="true" />

                {/* Ticket body */}
                <div className="rp-body">
                  {badge && (
                    <span className="rp-eyebrow" aria-hidden="true">
                      {badge}
                    </span>
                  )}
                  <button type="button" className="rp-title-btn" onClick={open}>
                    {offer.title}
                  </button>
                  {subTitle && <p className="rp-subtitle">{subTitle}</p>}
                  {descHtml && (
                    <div
                      className="rp-html"
                      dangerouslySetInnerHTML={{ __html: descHtml }}
                    />
                  )}
                  <span className="rp-perf" aria-hidden="true" />
                  <div className="rp-cta-row">
                    <button type="button" className="rp-cta" onClick={open}>
                      View Offer Details
                      <i
                        className="fa-solid fa-arrow-right"
                        aria-hidden="true"
                      />
                    </button>
                    {ctaLinks.map((link) => (
                      <a
                        key={link.href}
                        className="rp-cta-link"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={link.label}
                      >
                        {link.label}
                        <i
                          className="fa-solid fa-arrow-up-right-from-square"
                          aria-hidden="true"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Offer Details Dialog ── */}
      <Dialog
        open={openOffer !== null}
        onClose={() => setOpenOffer(null)}
        title={openOffer?.title}
        toolbar={
          flyer ? (
            <OfferFlyerBar file={flyer} title={detail?.downloadFile?.downloadTitle} />
          ) : undefined
        }
        loading={loading}
        width={760}
        maxWidth="min(92vw, 860px)"
        closeOnBackdropClick={false}
        footer={
          <button
            type="button"
            className={styles.dialogCloseBtn}
            onClick={() => setOpenOffer(null)}
          >
            Close
          </button>
        }
      >
        {error ? (
          <p className="blog-text mb-0">{error}</p>
        ) : detail ? (
          <>
            {openOffer?.photo && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                className={styles.heroImg}
                src={openOffer.photo}
                alt={openOffer.title}
              />
            )}

            {/* The Offer — short description first */}
            {detail.shortDescription?.trim() && (
              <section
                className={styles.offerPanel}
                aria-labelledby={`strip-offer-${detail.id}`}
              >
                <p className={styles.eyebrow} id={`strip-offer-${detail.id}`}>
                  <i className="fa-solid fa-tag" aria-hidden="true" /> The Offer
                </p>
                <div
                  className={styles.richHtml}
                  dangerouslySetInnerHTML={{ __html: detail.shortDescription }}
                />
              </section>
            )}

            {/* Details — full description below */}
            {detail.description?.trim() && (
              <>
                <hr className={styles.divider} />
                <section aria-labelledby={`strip-details-${detail.id}`}>
                  <p
                    className={styles.eyebrow}
                    id={`strip-details-${detail.id}`}
                  >
                    <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
                    Details
                  </p>
                  <div
                    className={styles.richHtml}
                    dangerouslySetInnerHTML={{ __html: detail.description }}
                  />
                </section>
              </>
            )}

            {/* Terms & conditions — the CMS copy carries its own heading, so
                this renders the HTML as-is rather than adding a duplicate. */}
            {terms && (
              <>
                <hr className={styles.divider} />
                <div
                  className={`${styles.richHtml} ${styles.terms}`}
                  dangerouslySetInnerHTML={{ __html: terms }}
                />
              </>
            )}

            {dialogRelations.length > 0 && (
              <div className="mt-3">
                <OfferRelations relations={dialogRelations} variant="full" />
              </div>
            )}

            {!detail.shortDescription?.trim() &&
              !detail.description?.trim() &&
              !terms &&
              !flyer && (
                <p className="blog-text mb-0">
                  No details available for this offer.
                </p>
              )}
          </>
        ) : null}
      </Dialog>

      {/* ── Styles ── */}
      <style>{`
        /* Section header */
        .offers-strip__header {
          text-align: center;
          margin-bottom: 24px;
        }
        .offers-strip__badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 16px;
          border-radius: 50px;
          background: var(--theme-color, #df1b23);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .8px;
          margin-bottom: 10px;
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .offers-strip__title {
          font-size: 26px;
          font-weight: 800;
          color: #1a1a2e;
          margin: 0;
        }
        .offers-strip__subtitle {
          font-size: 14px;
          color: #8a8a9a;
          margin: 4px 0 0;
        }

        /* ── RedPass — perforated deal coupon ── */
        .rp-card {
          position: relative;
          display: flex;
          align-items: stretch;
          width: 100%;
          height: 100%;
          background: #fff;
          border: 1px solid rgba(0,0,0,.07);
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0,0,0,.07);
          transition: transform .3s cubic-bezier(.25,.46,.45,.94), box-shadow .3s ease;
          animation: fade-up .5s ease both;
        }
        .rp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0,0,0,.14);
        }

        /* Photo stub — a button, stretches to full card height so the
           perforation aligns */
        .rp-stub {
          position: relative;
          flex-shrink: 0;
          width: 104px;
          align-self: stretch;
          overflow: hidden;
          border-radius: 12px 0 0 12px;
          padding: 0;
          border: none;
          /* Light placeholder so the reserved space isn't a blank gap before
             the image renders (the image fades in over it on desktop). */
          background: #eef2f6;
          cursor: pointer;
          display: block;
        }
        .rp-stub img {
          transition: transform .4s cubic-bezier(.25,.46,.45,.94) !important;
        }
        .rp-card:hover .rp-stub img { transform: scale(1.06); }
        .rp-stub:focus-visible {
          outline: 2px solid var(--theme-color, #df1b23);
          outline-offset: -2px;
        }

        /* The signature red spine */
        .rp-seam {
          flex-shrink: 0;
          width: 3px;
          align-self: stretch;
          background: var(--theme-color, #df1b23);
          transition: filter .3s ease;
        }
        .rp-card:hover .rp-seam { filter: brightness(1.12); }

        /* Ticket body */
        .rp-body {
          position: relative;
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          padding: 12px 14px 12px 16px;
          border-left: 1px dashed rgba(0,0,0,.18);
        }
        /* Perforation notches that "bite" the spine at top and bottom */
        .rp-body::before,
        .rp-body::after {
          content: "";
          position: absolute;
          left: 0;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #fff;
          transform: translateX(-50%);
        }
        .rp-body::before { top: -5px; }
        .rp-body::after { bottom: -5px; }

        .rp-eyebrow {
          align-self: flex-start;
          display: inline-block;
          margin-bottom: 7px;
          padding: 3px 9px;
          border-radius: 999px;
          background: var(--theme-color, #df1b23);
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .8px;
          text-transform: uppercase;
          line-height: 1.2;
        }
        .rp-title-btn {
          align-self: flex-start;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
          font: inherit;
          text-align: left;
          cursor: pointer;
          font-size: 15px;
          font-weight: 700;
          color: #1a1a2e;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          transition: color .2s ease;
        }
        .rp-card:hover .rp-title-btn { color: var(--theme-color, #df1b23); }
        .rp-title-btn:focus-visible {
          outline: 2px solid var(--theme-color, #df1b23);
          outline-offset: 2px;
          border-radius: 3px;
        }

        /* Offer subtitle — quiet grey line directly under the title. */
        .rp-subtitle {
          margin: 4px 0 0;
          font-size: 12px;
          font-weight: 500;
          line-height: 1.4;
          color: #6b7280;
          overflow-wrap: anywhere;
        }
        .rp-card--solo .rp-subtitle { font-size: 13.5px; margin-top: 6px; }

        /* Short description rendered as real HTML (full, not clamped) */
        .rp-html {
          margin: 6px 0 16px;
          font-size: 12.5px;
          color: #5a5a68;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }
        .rp-html :where(p, div, ul, ol, h1, h2, h3, h4) { margin: 0 0 .4rem; }
        .rp-html :where(p, div, ul, ol):last-child { margin-bottom: 0; }
        .rp-html ul, .rp-html ol { padding-left: 1.1rem; }
        /* Collapse CMS "blank line" spacer blocks (e.g. <div><b><br></b></div>,
           <div><br></div>, <p></p>) that would otherwise add a tall empty line
           at a normal line-height. Real paragraphs keep their .4rem spacing. */
        .rp-html :where(p, div):empty,
        .rp-html :where(p, div):has(> br:only-child),
        .rp-html :where(p, div):has(> b:only-child > br:only-child),
        .rp-html :where(p, div):has(> i:only-child > br:only-child),
        .rp-html :where(p, div):has(> span:only-child > br:only-child),
        .rp-html :where(p, div):has(> b:only-child:empty),
        .rp-html :where(p, div):has(> span:only-child:empty) {
          margin: 0;
          /* Small (not zero) break height: one blank line stays compact, but
             several stacked breaks still add up. */
          line-height: 8px;
        }
        .rp-html a {
          color: var(--theme-color, #df1b23);
          font-weight: 600;
          text-decoration: underline;
        }
        .rp-html strong, .rp-html b { font-weight: 800; color: #2a2a3a; }
        .rp-html sup { font-size: .7em; }
        .rp-html img { max-width: 100%; height: auto; }
        .rp-card--solo .rp-html { font-size: 14px; margin-top: 10px; }
        .rp-perf {
          margin-top: auto;
          margin-bottom: 12px;
          border-top: 1px dashed rgba(0,0,0,.14);
        }
        /* CTA row: primary "View Offer Details" + any extracted CTA links.
           Wraps cleanly when there are several / when the column is narrow. */
        .rp-cta-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        /* Primary action — solid brand pill */
        .rp-cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin: 0;
          padding: 6px 14px;
          border: 1px solid var(--theme-color, #df1b23);
          border-radius: 999px;
          background: var(--theme-color, #df1b23);
          color: #fff;
          font: inherit;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.25;
          text-transform: uppercase;
          letter-spacing: .02em;
          cursor: pointer;
          transition: gap .25s ease, box-shadow .25s ease;
        }
        .rp-card:hover .rp-cta {
          gap: 10px;
          box-shadow: 0 4px 12px rgba(223,27,35,.25);
        }
        .rp-cta:focus-visible {
          outline: 2px solid var(--theme-color, #df1b23);
          outline-offset: 2px;
        }
        .rp-cta i { font-size: .9em; flex-shrink: 0; }

        /* Extracted CTA links — outlined brand pills, open in a new tab */
        .rp-cta-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          max-width: 100%;
          margin: 0;
          padding: 6px 13px;
          border: 1px solid var(--theme-color, #df1b23);
          border-radius: 999px;
          background: #fff;
          color: var(--theme-color, #df1b23);
          font-size: 11.5px;
          font-weight: 700;
          line-height: 1.25;
          text-transform: uppercase;
          letter-spacing: .02em;
          text-decoration: none;
          transition: background .2s ease, color .2s ease, box-shadow .2s ease;
        }
        .rp-cta-link:hover {
          background: var(--theme-color, #df1b23);
          color: #fff;
          text-decoration: none;
          box-shadow: 0 4px 12px rgba(223,27,35,.25);
        }
        .rp-cta-link:focus-visible {
          outline: 2px solid var(--theme-color, #df1b23);
          outline-offset: 2px;
        }
        .rp-cta-link i { font-size: .82em; flex-shrink: 0; opacity: .85; }

        /* ── Solo (single offer) banner variant ── */
        .rp-card--solo { border-radius: 16px; }
        .rp-card--solo .rp-stub {
          width: 300px;
          border-radius: 16px 0 0 16px;
        }
        .rp-card--solo .rp-seam { width: 5px; }
        .rp-card--solo .rp-body { padding: 22px 28px; }
        .rp-card--solo .rp-body::before,
        .rp-card--solo .rp-body::after { width: 11px; height: 11px; }
        .rp-card--solo .rp-eyebrow { font-size: 11px; }
        .rp-card--solo .rp-title-btn { font-size: 22px; }
        .rp-card--solo .rp-cta {
          padding: 9px 22px;
          font-size: 13.5px;
          box-shadow: 0 4px 12px rgba(223,27,35,.22);
        }
        .rp-card--solo:hover .rp-cta { box-shadow: 0 6px 18px rgba(223,27,35,.30); }
        .rp-card--solo .rp-cta-link {
          padding: 8px 18px;
          font-size: 12.5px;
        }

        @media (max-width: 767.98px) {
          /* Phones: no photo — collapse the stub and the ticket's spine /
             perforation so the offer reads as a clean, full-width card. */
          .rp-stub { display: none; }
          .rp-seam { display: none; }
          .rp-body {
            border-left: none;
            padding-left: 16px;
          }
          .rp-body::before,
          .rp-body::after { display: none; }

          .rp-card--solo .rp-title-btn { font-size: 16px; }
          .rp-card--solo .rp-body { padding: 14px 16px; }
          .rp-card--solo .rp-cta,
          .rp-card--solo .rp-cta-link {
            width: 100%;
            justify-content: center;
          }
        }
        /* Phones: stack every CTA full-width for easy tapping */
        @media (max-width: 479.98px) {
          .rp-cta-row .rp-cta,
          .rp-cta-row .rp-cta-link {
            width: 100%;
            justify-content: center;
          }
        }

        /* Animations */
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(223,27,35,.4); }
          50%      { box-shadow: 0 0 0 8px rgba(223,27,35,0); }
        }

        /* Respect reduced-motion: no entrance, lift, zoom, or arrow slide */
        @media (prefers-reduced-motion: reduce) {
          .rp-card,
          .rp-stub img,
          .rp-cta,
          .rp-title-btn,
          .rp-seam,
          .offers-strip__badge {
            animation: none !important;
            transition: none !important;
          }
          .rp-card:hover { transform: none; }
          .rp-card:hover .rp-stub img { transform: none; }
        }
      `}</style>
    </div>
  );
}
